import { signalStore, withState, withMethods, patchState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, Observable, filter, from, pipe, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorStore } from '@application/errors/data-access';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';

import { IStandardMessage, TAccessControl, TAlertMessages, TEntryMessages, TExitMessages, TExpirationMessages, TSubscriptionStatusMessages, TAccessGroup, IMessageSettings, IAccessSettings, IAdvertisementSettings } from '../entities/access-settings.interface';
import { AccessSettingsService } from '../infra/access-settings.service';
import { SelfUserStore, PermissionNumber } from '@person/user/self/data-access';
import { tapResponse } from '@ngrx/operators';

const TEMP_AUTH_PERMISSION_GUARD_PATH = 'TEMPORARY_AUTH_MODAL.PERMISSION_GUARD';

export type TAcessStore = {
  AccessControl: TAccessControl;
  EntryMessages: TEntryMessages;
  ExitMessages: TExitMessages;
  ExpirationMessages: TExpirationMessages;
  SubscriptionStatusMessages: TSubscriptionStatusMessages;
  AlertMessages: TAlertMessages;
  lastEntryDate: TEntryMessages;
  standardMessages: IStandardMessage;
  showAccessGroupForm: boolean;
  accessGroup: TAccessGroup;
  AdvertisementSettings: IAdvertisementSettings | null;
};

export const initialAccessStore: TAcessStore = {
  AccessControl: null,
  EntryMessages: null,
  ExitMessages: null,
  ExpirationMessages: null,
  SubscriptionStatusMessages: null,
  AlertMessages: null,
  standardMessages: null,
  lastEntryDate: null,
  showAccessGroupForm: false,
  accessGroup: null,
  AdvertisementSettings: null,
};

export const AccessSettingsStore = signalStore(
  { providedIn: 'root' },
  withState<TAcessStore>(initialAccessStore),
  withMethods(
    (
      store,
      accessService = inject(AccessSettingsService),
      errorStore = inject(ErrorStore),
      selfUserStore = inject(SelfUserStore),
    ) => {
      const ensureAdjustPermission = () =>
        from(
          selfUserStore.ensurePermissions(
            [PermissionNumber.AjusteAtualizar],
            {
              title: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.ADJUST_SETTINGS.TITLE`,
              message: `${TEMP_AUTH_PERMISSION_GUARD_PATH}.ADJUST_SETTINGS.MESSAGE`,
            },
          ),
        );

      const guardAdjustPermission = <T>(requestKey: string, work: () => Observable<T>) =>
        ensureAdjustPermission().pipe(
          switchMap((hasPermission) => {
            if (!hasPermission) {
              patchState(store, setPropInit(requestKey));
              return EMPTY;
            }

            return work();
          }),
        );

      return {
        toggleAccessGroupFormModal: (show: boolean = !store?.showAccessGroupForm): void => {
          patchState(store, {
            showAccessGroupForm: show
          });
        },

        getAccessGroup: rxMethod<string>(pipe(
          tap(() => patchState(store, setPropLoading('getAccessGroup'))),
          switchMap((payload) => accessService.getAccessGroup(payload).pipe(
            tapResponse({
              next: (accessGroup) => {
                patchState(store, { accessGroup }, setPropLoaded('getAccessGroup'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('getAccessGroup', error));
              }
            })
          ))
        )),

        updateAccessGroup: rxMethod<TAccessGroup>(pipe(
          tap(() => patchState(store, setPropLoading('updateAccessGroup'))),
          switchMap((payload) => accessService.updateAccessGroup(payload).pipe(
            tapResponse({
              next: () => {
                patchState(store, setPropLoaded('updateAccessGroup'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('updateAccessGroup', error));
              }
            })
          ))
        )),
        createAccessGroup: rxMethod<TAccessGroup>(pipe(
          tap(() => patchState(store, setPropLoading('createAccessGroup'))),
          switchMap((payload) => accessService.createAccessGroup(payload).pipe(
            tapResponse({
              next: () => {
                patchState(store, setPropLoaded('createAccessGroup'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('createAccessGroup', error));
              }
            })
          ))
        )),
        deleteAccessGroup: rxMethod<string>(pipe(
          tap(() => patchState(store, setPropLoading('deleteAccessGroup'))),
          switchMap((payload) => accessService.deleteAccessGroup(payload).pipe(
            tapResponse({
              next: () => {
                patchState(store, setPropLoaded('deleteAccessGroup'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('deleteAccessGroup', error));
              }
            })
          ))
        )),
        getControlAccess: rxMethod<void>(pipe(
          tap(() => patchState(store, setPropLoading('getControlAccess'))),
          switchMap(() => accessService.getControlAccess().pipe(
            tapResponse({
              next: (response: IAccessSettings) => {
                const accessControl: TAccessControl = {
                  AccessSettingsId: response.AccessSettingsId,
                  DaysToReleaseDefaulters: response.DaysToReleaseDefaulters,
                  DaysToReleaseVisitorAccess: response.DaysToReleaseVisitorAccess,
                  PrintTraining: response.PrintTraining,
                  BlockDefaultingSales: response.BlockDefaultingSales,
                  BlockDefaultingServices: response.BlockDefaultingServices,
                  BlockWithoutReservation: response.BlockWithoutReservation,
                  BlockKeyboardCode: response.BlockKeyboardCode,
                  AllowReentry: response.AllowReentry,
                  CodeIdentifier: response.CodeIdentifier,
                  PlayHappyBirthday: response.PlayHappyBirthday,
                  SecondsToCleanMonitor: response.SecondsToCleanMonitor,
                  ShowLastNameInIdentification: response.ShowLastNameInIdentification,
                  ShowQRCodeAccess: response.ShowQRCodeAccess,
                  UpdatedOn: response.UpdatedOn ?? null,
                };
                patchState(store, { AccessControl: accessControl }, setPropLoaded('getControlAccess'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('getControlAccess', error));
              }
            })
          ))
        )),
        getAllAccessMessages: rxMethod<void>(pipe(
          tap(() => patchState(store, setPropLoading('getAllAccessMessages'))),
          switchMap(() => accessService.getAllAccessMessages().pipe(
            tapResponse({
              next: (response: IMessageSettings) => {
                patchState(store, {
                  EntryMessages: {
                    MessageSettingsId: response.MessageSettingsId,
                    EntryCustomer: response.EntryCustomer,
                    EntryCustomerPass: response.EntryCustomerPass,
                    EntryEmployee: response.EntryEmployee,
                    ReentryDenied: response.ReentryDenied,
                  },
                  ExitMessages: {
                    MessageSettingsId: response.MessageSettingsId,
                    MorningExit: response.MorningExit,
                    NightExit: response.NightExit,
                    AfternoonExit: response.AfternoonExit,
                  },
                  ExpirationMessages: {
                    MessageSettingsId: response.MessageSettingsId,
                    EvaluationExpired: response.EvaluationExpired,
                    MedicalExamExpired: response.MedicalExamExpired,
                    OutstandingPayment: response.OutstandingPayment,
                    PassesExhausted: response.PassesExhausted,
                  },
                  SubscriptionStatusMessages: {
                    MessageSettingsId: response.MessageSettingsId,
                    SubscriptionClosed: response.SubscriptionClosed,
                    SubscriptionRecess: response.SubscriptionRecess,
                    SubscriptionLocked: response.SubscriptionLocked,
                    WithoutSubscription: response.WithoutSubscription,
                  },
                  AlertMessages: {
                    MessageSettingsId: response.MessageSettingsId,
                    Birthday: response.Birthday,
                    BlockWithoutReservation: response.BlockWithoutReservation,
                    OutOfHours: response.OutOfHours,
                    PersonInactive: response.PersonInactive,
                    PersonNotFound: response.PersonNotFound,
                  },
                },
                setPropLoaded('getAllAccessMessages'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('getAllAccessMessages', error));
              }
            })
          ))
        )),
        getStandardMessages: rxMethod<void>(pipe(
          filter(() => !store.standardMessages()),
          tap(() => patchState(store, setPropLoading('getStandardMessages'))),
          switchMap(() => accessService.getStandardMessages().pipe(
            tapResponse({
              next: (standardMessages: IStandardMessage) => {
                patchState(store, { standardMessages }, setPropLoaded('getStandardMessages'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('getStandardMessages', error));
              }
            })
          ))
        )),
        updateEntryMessages: rxMethod<TEntryMessages>(pipe(
          tap(() => patchState(store, setPropLoading('updateEntryMessages'))),
          switchMap((payload) => guardAdjustPermission('updateEntryMessages', () => {
            const messageSettings: Partial<IMessageSettings> = {
              MessageSettingsId: payload.MessageSettingsId,
              EntryCustomer: payload.EntryCustomer,
              EntryCustomerPass: payload.EntryCustomerPass,
              EntryEmployee: payload.EntryEmployee,
              ReentryDenied: payload.ReentryDenied,
            };
            return accessService.updateMessageSettings(messageSettings).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    EntryMessages: {
                      MessageSettingsId: response.MessageSettingsId,
                      EntryCustomer: response.EntryCustomer,
                      EntryCustomerPass: response.EntryCustomerPass,
                      EntryEmployee: response.EntryEmployee,
                      ReentryDenied: response.ReentryDenied,
                    }
                  },
                    setPropLoaded('updateEntryMessages'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateEntryMessages', error));
                }
              })
            );
          }))
        )),
        updateExitMessages: rxMethod<TExitMessages>(pipe(
          tap(() => patchState(store, setPropLoading('updateExitMessages'))),
          switchMap((payload) => guardAdjustPermission('updateExitMessages', () => {
            const messageSettings: Partial<IMessageSettings> = {
              MessageSettingsId: payload.MessageSettingsId,
              MorningExit: payload.MorningExit,
              NightExit: payload.NightExit,
              AfternoonExit: payload.AfternoonExit,
            };
            return accessService.updateMessageSettings(messageSettings).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    ExitMessages: {
                      MessageSettingsId: response.MessageSettingsId,
                      MorningExit: response.MorningExit,
                      NightExit: response.NightExit,
                      AfternoonExit: response.AfternoonExit,
                    }
                  },
                    setPropLoaded('updateExitMessages'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateExitMessages', error));
                }
              })
            );
          }))
        )),
        updateExpirationMessages: rxMethod<TExpirationMessages>(pipe(
          tap(() => patchState(store, setPropLoading('updateExpirationMessages'))),
          switchMap((payload) => guardAdjustPermission('updateExpirationMessages', () => {
            const messageSettings: Partial<IMessageSettings> = {
              MessageSettingsId: payload.MessageSettingsId,
              EvaluationExpired: payload.EvaluationExpired,
              MedicalExamExpired: payload.MedicalExamExpired,
              OutstandingPayment: payload.OutstandingPayment,
              PassesExhausted: payload.PassesExhausted,
            };
            return accessService.updateMessageSettings(messageSettings).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    ExpirationMessages: {
                      MessageSettingsId: response.MessageSettingsId,
                      EvaluationExpired: response.EvaluationExpired,
                      MedicalExamExpired: response.MedicalExamExpired,
                      OutstandingPayment: response.OutstandingPayment,
                      PassesExhausted: response.PassesExhausted,
                    }
                  },
                    setPropLoaded('updateExpirationMessages'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateExpirationMessages', error));
                }
              })
            );
          }))
        )),
        updateSubscriptionStatusMessages: rxMethod<TSubscriptionStatusMessages>(pipe(
          tap(() => patchState(store, setPropLoading('updateSubscriptionStatusMessages'))),
          switchMap((payload) => guardAdjustPermission('updateSubscriptionStatusMessages', () => {
            const messageSettings: Partial<IMessageSettings> = {
              MessageSettingsId: payload.MessageSettingsId,
              SubscriptionClosed: payload.SubscriptionClosed,
              SubscriptionRecess: payload.SubscriptionRecess,
              SubscriptionLocked: payload.SubscriptionLocked,
              WithoutSubscription: payload.WithoutSubscription,
            };
            return accessService.updateMessageSettings(messageSettings).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    SubscriptionStatusMessages: {
                      MessageSettingsId: response.MessageSettingsId,
                      SubscriptionClosed: response.SubscriptionClosed,
                      SubscriptionRecess: response.SubscriptionRecess,
                      SubscriptionLocked: response.SubscriptionLocked,
                      WithoutSubscription: response.WithoutSubscription,
                    }
                  },
                    setPropLoaded('updateSubscriptionStatusMessages'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateSubscriptionStatusMessages', error));
                }
              })
            );
          }))
        )),
        updateAlertMessages: rxMethod<TAlertMessages>(pipe(
          tap(() => patchState(store, setPropLoading('updateAlertMessages'))),
          switchMap((payload) => guardAdjustPermission('updateAlertMessages', () => {
            const messageSettings: Partial<IMessageSettings> = {
              MessageSettingsId: payload.MessageSettingsId,
              Birthday: payload.Birthday,
              BlockWithoutReservation: payload.BlockWithoutReservation,
              OutOfHours: payload.OutOfHours,
              PersonInactive: payload.PersonInactive,
              PersonNotFound: payload.PersonNotFound,
            };
            return accessService.updateMessageSettings(messageSettings).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, {
                    AlertMessages: {
                      MessageSettingsId: response.MessageSettingsId,
                      Birthday: response.Birthday,
                      BlockWithoutReservation: response.BlockWithoutReservation,
                      OutOfHours: response.OutOfHours,
                      PersonInactive: response.PersonInactive,
                      PersonNotFound: response.PersonNotFound,
                    }
                  },
                    setPropLoaded('updateAlertMessages'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateAlertMessages', error));
                }
              })
            );
          }))
        )),
        updateAccessControll: rxMethod<TAccessControl>(pipe(
          tap(() => patchState(store, setPropLoading('updateAccessControll'))),
          switchMap((payload) => guardAdjustPermission('updateAccessControll', () => {
            const accessSettings: Partial<IAccessSettings> = {
              AccessSettingsId: payload.AccessSettingsId,
              DaysToReleaseDefaulters: payload.DaysToReleaseDefaulters,
              DaysToReleaseVisitorAccess: payload.DaysToReleaseVisitorAccess,
              PrintTraining: payload.PrintTraining,
              BlockDefaultingSales: payload.BlockDefaultingSales,
              BlockDefaultingServices: payload.BlockDefaultingServices,
              BlockWithoutReservation: payload.BlockWithoutReservation,
              BlockKeyboardCode: payload.BlockKeyboardCode,
              AllowReentry: payload.AllowReentry,
              CodeIdentifier: payload.CodeIdentifier,
              PlayHappyBirthday: payload.PlayHappyBirthday,
              SecondsToCleanMonitor: payload.SecondsToCleanMonitor,
              ShowLastNameInIdentification: payload.ShowLastNameInIdentification,
              ShowQRCodeAccess: payload.ShowQRCodeAccess,
            };
            return accessService.updateAccessControl(accessSettings).pipe(
              tapResponse({
                next: (response) => {
                  const accessControl: TAccessControl = {
                    AccessSettingsId: response.AccessSettingsId,
                    DaysToReleaseDefaulters: response.DaysToReleaseDefaulters,
                    DaysToReleaseVisitorAccess: response.DaysToReleaseVisitorAccess,
                    PrintTraining: response.PrintTraining,
                    BlockDefaultingSales: response.BlockDefaultingSales,
                    BlockDefaultingServices: response.BlockDefaultingServices,
                    BlockWithoutReservation: response.BlockWithoutReservation,
                    BlockKeyboardCode: response.BlockKeyboardCode,
                    AllowReentry: response.AllowReentry,
                    CodeIdentifier: response.CodeIdentifier,
                    PlayHappyBirthday: response.PlayHappyBirthday,
                    SecondsToCleanMonitor: response.SecondsToCleanMonitor,
                    ShowLastNameInIdentification: response.ShowLastNameInIdentification,
                    ShowQRCodeAccess: response.ShowQRCodeAccess,
                    UpdatedOn: response.UpdatedOn ?? null,
                  };
                  patchState(store, { AccessControl: accessControl },
                    setPropLoaded('updateAccessControll'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, setPropError('updateAccessControll', error));
                }
              })
            );
          }))
        )),
        captureFingerprint: rxMethod<string>(pipe(
          tap(() => patchState(store, setPropLoading('captureFingerprint'))),
          switchMap((payload) => accessService.captureFingerprint(payload).pipe(
            tapResponse({
              next: () => {
                patchState(store, setPropLoaded('captureFingerprint'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('captureFingerprint', error));
              }
            })
          ))
        )),
        captureBiometry: rxMethod<string>(pipe(
          tap(() => patchState(store, setPropLoading('captureBiometry'))),
          switchMap((payload) => accessService.captureBiometry(payload).pipe(
            tapResponse({
              next: () => {
                patchState(store, setPropLoaded('captureBiometry'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('captureBiometry', error));
              }
            })
          ))
        )),
        blankAccessGroup: () => {
          patchState(store, {
            accessGroup: null,
            ...setPropInit(
              'updateAccessGroup',
              'createAccessGroup',
              'deleteAccessGroup'
            )
          });
        },
        getAdvertisementSettings: rxMethod<void>(pipe(
          tap(() => patchState(store, setPropLoading('getAdvertisementSettings'))),
          switchMap(() => accessService.getAdvertisementSettings().pipe(
            tapResponse({
              next: (response) => {
                patchState(store, { AdvertisementSettings: response }, setPropLoaded('getAdvertisementSettings'));
              },
              error: (error: HttpErrorResponse) => {
                errorStore.setHttpError(error);
                patchState(store, setPropError('getAdvertisementSettings', error));
              }
            })
          ))
        )),
        updateAdvertisementSettings: rxMethod<Partial<IAdvertisementSettings>>(pipe(
          tap(() => patchState(store, setPropLoading('updateAdvertisementSettings'))),
          switchMap((payload) => guardAdjustPermission('updateAdvertisementSettings', () => {
            return accessService.updateAdvertisementSettings(payload).pipe(
              tapResponse({
                next: (response) => {
                  patchState(store, { AdvertisementSettings: response }, setPropLoaded('updateAdvertisementSettings'));
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('updateAdvertisementSettings', error));
                }
              })
            );
          }))
        )),
        resetReqState: () => {
          patchState(store, setPropInit(
            'getControlAccess',
            'getAllAccessMessages',
            'getStandardMessages',
            'updateEntryMessages',
            'updateExitMessages',
            'updateExpirationMessages',
            'updateSubscriptionStatusMessages',
            'updateAlertMessages',
            'updateAccessControll',
            'captureFingerprint',
            'captureBiometry',
            'getAdvertisementSettings',
            'updateAdvertisementSettings'
          ));
        }
      };
    }
  ),
  withReqState(
    'getControlAccess',
    'getAllAccessMessages',
    'getStandardMessages',
    'updateEntryMessages',
    'updateExitMessages',
    'updateExpirationMessages',
    'updateSubscriptionStatusMessages',
    'updateAlertMessages',
    'updateAccessControll',
    'captureFingerprint',
    'captureBiometry',
    'getAccessGroup',
    'updateAccessGroup',
    'createAccessGroup',
    'deleteAccessGroup',
    'getAdvertisementSettings',
    'updateAdvertisementSettings'
  )
);
